// ============================================================
// TMR Portal Admin — Contingência → Código coringa (rota /contingencia/codigo-coringa) (31 §9)
// ============================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, pageCount } from '@tmr/api-client';
import { FilterTabs, PageHeader, Table, Td } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Notice } from '../../components/Notice';
import { Pagination } from '../../components/Pagination';
import { TextField } from '../../components/fields';
import { Btn, StatusBadge } from '../../components/primitives';
import { Icons } from '../../lib/icons';
import { isValidCPF, maskCPF } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  useContingencyRequests,
  useContingencyTypes,
  useDecideContingencyRequest,
  useIssueApprovedWildcardCode,
  useRequestWildcardCode,
} from '../../data/contingency/queries';
import type { ContingencyRequest, ContingencyRequestStatus, ContingencyType, WildcardCodeResponse } from '../../data/contingency/contract';
import {
  CONTINGENCY_TYPE_COPY,
  REQUEST_STATUS_BADGE,
  REQUEST_STATUS_OPTIONS,
  codeValidity,
  formatMoment,
  typeSituation,
} from '../../data/contingency/status';
import { DecisionDialog } from './DecisionDialog';
import { WildcardCodeIssuedDialog } from './WildcardCodeIssuedDialog';

/** Requests per page of the list. */
const PAGE_SIZE = 20;

/** Props for {@link WildcardCodePage}. */
export interface WildcardCodePageProps {
  /**
   * The operator of the session — the list offers "gerar código" on their own approved requests and keeps the
   * approve/reject buttons off them. The server refuses either way; this only avoids offering a 403.
   */
  operatorId: string;
}

/**
 * The wildcard code (código coringa): the form that asks for one, and every request with where it stands. Who
 * holds `contingency.wildcard_code.create` asks; whoever holds `contingency.manage` decides the requests of others
 * while approval is on; the requester generates the code of an approved request — and sees it once.
 */
export function WildcardCodePage({ operatorId }: WildcardCodePageProps) {
  const canAccess = usePermission(PERMISSIONS.contingencyAccess);
  const types = useContingencyTypes(canAccess);
  const [issued, setIssued] = useState<WildcardCodeResponse | null>(null);
  const copy = CONTINGENCY_TYPE_COPY.wildcard_code;

  if (!canAccess) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState message="Você não tem permissão para ver a Contingência." hint="Peça o acesso a quem administra os papéis." />
        </div>
      </div>
    );
  }

  const type = types.data?.items.find((item) => item.key === 'wildcard_code');

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Contingência"
        title={copy.name}
        desc={copy.description}
        actions={
          <Link to="/contingencia" style={{ textDecoration: 'none' }}>
            <Btn variant="ghost" icon={Icons.chevronL}>
              Contingência
            </Btn>
          </Link>
        }
      />

      {types.isPending ? (
        <Skeleton rows={2} />
      ) : types.isError ? (
        <ErrorState error={types.error} onRetry={() => void types.refetch()} />
      ) : type ? (
        <RequestForm type={type} onIssued={setIssued} />
      ) : null}

      <RequestList operatorId={operatorId} onIssued={setIssued} />

      {issued?.code && <WildcardCodeIssuedDialog response={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}

/**
 * The form: the patient's CPF and why. What the button says follows the setting — with approval the request
 * waits for a manager; without, the code comes back at once. Every request costs the patient their current code,
 * so the form validates everything it can before sending.
 */
function RequestForm({ type, onIssued }: { type: ContingencyType; onIssued: (response: WildcardCodeResponse) => void }) {
  const canCreate = usePermission(PERMISSIONS.contingencyWildcardCodeCreate);
  const request = useRequestWildcardCode();
  const [cpf, setCpf] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const situation = typeSituation(type);

  const cpfError = touched && !isValidCPF(cpf) ? 'Informe um CPF válido.' : undefined;
  const reasonError = touched && !reason.trim() ? 'Informe o motivo do pedido.' : undefined;

  if (!canCreate) {
    return (
      <div className="pa-card" style={{ padding: '18px 20px' }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-muted)' }}>
          {situation.label}. Você pode acompanhar os pedidos abaixo; para gerar um código, peça a permissão a quem administra os papéis.
        </p>
      </div>
    );
  }

  if (!type.enabled) {
    return (
      <Notice tone="neutral">
        O código coringa está desligado. Um gestor de Contingência pode ligá-lo no painel de Contingência.
      </Notice>
    );
  }

  if (!type.configured) {
    return (
      <Notice tone="warning">
        Este ambiente ainda não está configurado para gerar o código coringa: falta o endereço ou a chave do checkout do sistema antigo.
      </Notice>
    );
  }

  const submit = () => {
    setTouched(true);
    setWaiting(false);
    if (!isValidCPF(cpf) || !reason.trim()) return;

    request.mutate(
      { cpf, reason: reason.trim() },
      {
        onSuccess: (response) => {
          setCpf('');
          setReason('');
          setTouched(false);
          if (response.code) onIssued(response);
          else setWaiting(true);
        },
      },
    );
  };

  return (
    <div className="pa-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, color: 'var(--ink-900)' }}>
          {type.requiresApproval ? 'Pedir um código coringa' : 'Gerar um código coringa'}
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)' }}>
          {type.requiresApproval
            ? 'O pedido espera um gestor de Contingência. Depois de aprovado, você gera o código na lista abaixo.'
            : 'O código é gerado na hora e aparece uma única vez. Confira as vendas antes de passá-lo ao paciente.'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr', gap: 14 }}>
        <TextField
          label="CPF do paciente"
          value={cpf}
          inputMode="numeric"
          required
          error={cpfError}
          disabled={request.isPending}
          onChange={(value) => setCpf(maskCPF(value))}
        />
        <TextField
          label="Motivo"
          value={reason}
          required
          maxLength={500}
          error={reasonError}
          disabled={request.isPending}
          placeholder="Ex.: o código não chega no WhatsApp do paciente"
          onChange={setReason}
        />
      </div>

      {request.isError && (
        <Notice tone="danger">
          {request.error instanceof ApiError ? request.error.detail : 'Não foi possível gerar o código coringa.'}
        </Notice>
      )}
      {waiting && (
        <Notice tone="success">Pedido enviado. Ele aparece na lista como aguardando aprovação.</Notice>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn variant="primary" icon={type.requiresApproval ? Icons.send : Icons.zap} onClick={submit} disabled={request.isPending}>
          {request.isPending ? 'Enviando…' : type.requiresApproval ? 'Pedir aprovação' : 'Gerar código'}
        </Btn>
      </div>
    </div>
  );
}

/** The list's tab for "every status". */
const ALL = 'all';

/** A decision the manager is about to take. */
interface PendingDecision {
  request: ContingencyRequest;
  outcome: 'approve' | 'reject';
}

/**
 * Every wildcard code request, newest first, with what each one is waiting for. The actions sit on the row they
 * act on: the manager decides the requests of others; the requester generates the code of their own approved one.
 */
function RequestList({ operatorId, onIssued }: { operatorId: string; onIssued: (response: WildcardCodeResponse) => void }) {
  const canManage = usePermission(PERMISSIONS.contingencyManage);
  const canCreate = usePermission(PERMISSIONS.contingencyWildcardCodeCreate);
  const [status, setStatus] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [deciding, setDeciding] = useState<PendingDecision | null>(null);
  const issue = useIssueApprovedWildcardCode();
  const decide = useDecideContingencyRequest();

  const query = useContingencyRequests({
    type: 'wildcard_code',
    status: status === ALL ? undefined : (status as ContingencyRequestStatus),
    page,
    pageSize: PAGE_SIZE,
  });

  const changeStatus = (next: string) => {
    setStatus(next);
    setPage(1);
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, color: 'var(--ink-900)' }}>Pedidos</h2>
        <FilterTabs
          tabs={[{ value: ALL, label: 'Todos' }, ...REQUEST_STATUS_OPTIONS]}
          value={status}
          onChange={changeStatus}
        />
      </div>

      {issue.isError && (
        <Notice tone="danger">
          {issue.error instanceof ApiError ? issue.error.detail : 'Não foi possível gerar o código coringa.'}
        </Notice>
      )}

      {query.isPending ? (
        <Skeleton rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            message={status === ALL ? 'Nenhum código coringa pedido ainda.' : 'Nenhum pedido nesta situação.'}
            hint={status === ALL ? 'Cada código gerado ou pedido aparece aqui, com quem pediu e por quê.' : undefined}
          />
        </div>
      ) : (
        <>
          <Table
            columns={[
              { label: 'Paciente' },
              { label: 'Motivo' },
              { label: 'Pedido' },
              { label: 'Situação' },
              { label: '', align: 'right' },
            ]}
          >
            {query.data.items.map((request) => {
              const own = request.requestedBy.userId === operatorId;
              return (
                <tr key={request.id} className="pa-row">
                  <Td>
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{request.patientCpfMasked}</span>
                    {request.sales.length > 0 && (
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                        {request.sales[0].patientName} · {request.sales.map((sale) => sale.clinic).join(', ')}
                      </span>
                    )}
                  </Td>
                  <Td style={{ maxWidth: 320 }}>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{request.reason}</span>
                  </Td>
                  <Td>
                    {request.requestedBy.name}
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-subtle)' }}>{formatMoment(request.requestedAt)}</span>
                  </Td>
                  <Td>
                    <StatusBadge status={REQUEST_STATUS_BADGE[request.status]} />
                    <RequestDetail request={request} />
                  </Td>
                  <Td align="right">
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {request.status === 'PendingApproval' && canManage && !own && (
                        <>
                          <Btn size="sm" variant="secondary" onClick={() => setDeciding({ request, outcome: 'reject' })}>
                            Reprovar
                          </Btn>
                          <Btn size="sm" variant="primary" icon={Icons.check} onClick={() => setDeciding({ request, outcome: 'approve' })}>
                            Aprovar
                          </Btn>
                        </>
                      )}
                      {request.status === 'Approved' && own && canCreate && (
                        <Btn
                          size="sm"
                          variant="primary"
                          icon={Icons.zap}
                          disabled={issue.isPending}
                          onClick={() => issue.mutate(request.id, { onSuccess: onIssued })}
                        >
                          {issue.isPending && issue.variables === request.id ? 'Gerando…' : 'Gerar código'}
                        </Btn>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
          <Pagination page={query.data.page} pageCount={pageCount(query.data)} total={query.data.totalCount} onChange={setPage} />
        </>
      )}

      {deciding && (
        <DecisionDialog
          request={deciding.request}
          outcome={deciding.outcome}
          pending={decide.isPending}
          error={decide.isError ? (decide.error instanceof ApiError ? decide.error.detail : 'Não foi possível registrar a decisão.') : null}
          onConfirm={(note) =>
            decide.mutate(
              { requestId: deciding.request.id, outcome: deciding.outcome, note },
              { onSuccess: () => setDeciding(null) },
            )
          }
          onClose={() => {
            decide.reset();
            setDeciding(null);
          }}
        />
      )}
    </section>
  );
}

/** The line under a request's badge: who decided, or until when its code works. */
function RequestDetail({ request }: { request: ContingencyRequest }) {
  const line =
    request.status === 'Issued'
      ? codeValidity(request.codeExpiresAt)
      : request.decidedBy && request.decidedAt
        ? `${request.status === 'Rejected' ? 'Reprovado' : 'Aprovado'} por ${request.decidedBy.name} em ${formatMoment(request.decidedAt)}`
        : null;

  return (
    <>
      {line && <span style={{ display: 'block', marginTop: 4, fontSize: 12.5, color: 'var(--fg-subtle)' }}>{line}</span>}
      {request.decisionNote && (
        <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--fg-muted)', whiteSpace: 'pre-wrap' }}>
          “{request.decisionNote}”
        </span>
      )}
    </>
  );
}
